import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, Btn, PageHead, StatusDot } from "@/components/kit";
import { CalendarClock, Play } from "lucide-react";

const scoreColor = (s) => (s >= 75 ? "#39FF14" : s >= 50 ? "#FFD700" : "#FF003C");

export default function Strategy() {
    const qc = useQueryClient();
    const [channelId, setChannelId] = useState("");
    const [days, setDays] = useState(7);

    const { data: channels = [] } = useQuery({
        queryKey: ["channels"],
        queryFn: async () => (await api.get("/channels")).data,
    });
    const { data: calendar = [] } = useQuery({
        queryKey: ["calendar"],
        queryFn: async () => (await api.get("/calendar")).data,
        refetchInterval: 5000,
    });

    const plan = useMutation({
        mutationFn: async () =>
            (await api.post("/strategy/plan", { channel_id: channelId, days: Number(days) })).data,
        onSuccess: (d) => {
            if (d.queued) {
                toast.success(
                    `Calendario de ${d.days} días en generación (~1-2 min) — los items irán apareciendo abajo`,
                    { duration: 8000 },
                );
            } else {
                toast.success("Calendario 24/7 generado por darkzOGx → Minimax");
            }
            qc.invalidateQueries({ queryKey: ["calendar"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo planeando estrategia"),
    });

    const produce = useMutation({
        mutationFn: async (id) => (await api.post(`/calendar/${id}/produce`)).data,
        onSuccess: () => {
            toast.success("Producción lanzada en Pixelle-Video");
            qc.invalidateQueries({ queryKey: ["calendar"] });
        },
        onError: () => toast.error("Fallo lanzando producción"),
    });

    const { data: autoDaily } = useQuery({
        queryKey: ["auto-daily"],
        queryFn: async () => (await api.get("/governance/auto-daily")).data,
    });
    const challenge = useMutation({
        mutationFn: async () =>
            (await api.post("/strategy/challenge", { channel_id: channelId, days: 30 })).data,
        onSuccess: (d) => {
            toast.success(
                `Reto de 30 días encolado: ${d.videos} videos (1 short + 1 long/día). Se generan en segundo plano.`
            );
            setTimeout(() => qc.invalidateQueries({ queryKey: ["calendar"] }), 5000);
        },
        onError: () => toast.error("No se pudo crear el reto de 30 días"),
    });

    const toggleDaily = useMutation({
        mutationFn: async (enabled) =>
            (await api.post("/governance/auto-daily", { enabled })).data,
        onSuccess: (d) => {
            toast.success(d.enabled
                ? "Producción diaria ACTIVADA — publicará solo los items agendados de hoy"
                : "Producción diaria desactivada");
            qc.invalidateQueries({ queryKey: ["auto-daily"] });
        },
        onError: () => toast.error("No se pudo cambiar el modo diario"),
    });

    const { data: dubbing } = useQuery({
        queryKey: ["dubbing"],
        queryFn: async () => (await api.get("/governance/dubbing")).data,
    });
    const saveDub = useMutation({
        mutationFn: async ({ enabled, languages }) =>
            (await api.post("/governance/dubbing", { enabled, languages })).data,
        onSuccess: (d) => {
            toast.success(d.enabled
                ? `Doblaje automático ACTIVADO (${["es", ...d.languages].map((l) => l.toUpperCase()).join(" · ")})`
                : "Doblaje automático desactivado");
            qc.invalidateQueries({ queryKey: ["dubbing"] });
        },
        onError: () => toast.error("No se pudo actualizar el doblaje"),
    });

    const { data: qcCfg } = useQuery({
        queryKey: ["qc-gate"],
        queryFn: async () => (await api.get("/governance/qc")).data,
    });
    const saveQc = useMutation({
        mutationFn: async ({ enabled }) => (await api.post("/governance/qc", { enabled })).data,
        onSuccess: (d) => {
            toast.success(d.enabled
                ? "Control de calidad ACTIVADO: cada vídeo se revisa y regenera si falla antes de subir"
                : "Control de calidad desactivado");
            qc.invalidateQueries({ queryKey: ["qc-gate"] });
        },
        onError: () => toast.error("No se pudo actualizar el control de calidad"),
    });
    const DUB_LANGS = [
        { code: "en", label: "Inglés", rpm: "RPM alto" },
        { code: "pt", label: "Portugués", rpm: "" },
        { code: "fr", label: "Francés", rpm: "RPM alto" },
        { code: "de", label: "Alemán", rpm: "RPM muy alto" },
    ];
    const curLangs = dubbing?.languages || ["en", "pt"];
    const toggleLang = (code) => {
        const next = curLangs.includes(code)
            ? curLangs.filter((l) => l !== code)
            : [...curLangs, code];
        saveDub.mutate({ enabled: dubbing?.enabled ?? true, languages: next.length ? next : ["en"] });
    };

    return (
        <div data-testid="strategy-page">
            <PageHead
                title="MOTOR DE ESTRATEGIA"
                sub="Scheduler 24/7. darkzOGx → Minimax agenda el tema del día con scoring de potencial por canal, pilar y formato."
            />

            <Panel title="Generar calendario" testId="strategy-form" className="mb-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-center">
                    <select
                        data-testid="strategy-channel-select"
                        value={channelId}
                        onChange={(e) => setChannelId(e.target.value)}
                        className="flex-1 border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                    >
                        <option value="">Selecciona un canal…</option>
                        {channels.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name} ({c.handle})
                            </option>
                        ))}
                    </select>
                    <select
                        data-testid="strategy-days-select"
                        value={days}
                        onChange={(e) => setDays(e.target.value)}
                        className="border border-input bg-background px-3 py-2 text-sm outline-none focus:border-primary"
                    >
                        {[7, 14, 21, 30].map((d) => (
                            <option key={d} value={d}>
                                {d} días
                            </option>
                        ))}
                    </select>
                    <Btn
                        testId="strategy-plan-btn"
                        onClick={() => channelId && plan.mutate()}
                        disabled={plan.isPending || !channelId}
                    >
                        <span className="flex items-center gap-2">
                            <CalendarClock size={14} />
                            {plan.isPending ? "Planeando…" : "Planear"}
                        </span>
                    </Btn>
                    <button
                        data-testid="strategy-challenge-btn"
                        onClick={() => channelId && challenge.mutate()}
                        disabled={challenge.isPending || !channelId}
                        className="flex items-center justify-center gap-2 border border-[#C81D25] bg-[#C81D25] px-4 py-2 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-transparent hover:text-[#ff5964] disabled:opacity-40"
                    >
                        <Play size={14} />
                        {challenge.isPending ? "Creando…" : "Reto 30 días"}
                    </button>
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                    "Reto 30 días" agenda <b>1 short + 1 long cada día</b> (60 videos) para disparar
                    suscriptores. Se producen a la cola de <b>Aprobación</b>; nada se publica sin tu OK.
                </p>
            </Panel>

            <Panel title="Producción diaria automática" testId="auto-daily-panel" className="mb-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-xl text-sm text-muted-foreground">
                        Publica solo los videos agendados para hoy, cada día, sin que muevas un dedo.
                        La constancia es la clave del algoritmo de Shorts para ganar suscriptores.
                    </p>
                    <button
                        data-testid="auto-daily-toggle-btn"
                        onClick={() => toggleDaily.mutate(!autoDaily?.enabled)}
                        disabled={toggleDaily.isPending}
                        className={`flex shrink-0 items-center gap-2 border px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${
                            autoDaily?.enabled
                                ? "border-primary bg-primary text-black"
                                : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                        }`}
                    >
                        <StatusDot color={autoDaily?.enabled ? "#000" : "#39FF14"} pulse={autoDaily?.enabled} />
                        {autoDaily?.enabled ? "Diario: ACTIVO" : "Activar producción diaria"}
                    </button>
                </div>
            </Panel>

            <Panel title="Doblaje multilingüe automático" testId="dubbing-panel" className="mb-6 border-primary/60">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-xl text-sm text-muted-foreground">
                        Cada video nuevo se replica <b>solo</b> en los idiomas que elijas (voz + subtítulos +
                        textos traducidos + miniatura con cara IA propia), reutilizando los mismos visuales, y
                        se sube al mismo canal. Un espectador en cada país te ve en su idioma —{" "}
                        <b>sin que subas nada</b>. Cada idioma va a su propia playlist.
                    </p>
                    <button
                        data-testid="dubbing-toggle-btn"
                        onClick={() => saveDub.mutate({ enabled: !dubbing?.enabled, languages: curLangs })}
                        disabled={saveDub.isPending}
                        className={`flex shrink-0 items-center gap-2 border px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${
                            dubbing?.enabled
                                ? "border-primary bg-primary text-black"
                                : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                        }`}
                    >
                        <StatusDot color={dubbing?.enabled ? "#000" : "#39FF14"} pulse={dubbing?.enabled} />
                        {dubbing?.enabled ? "Doblaje: ACTIVO" : "Activar doblaje automático"}
                    </button>
                </div>
                <div className="mt-4 flex flex-wrap gap-2" data-testid="dubbing-langs">
                    <span className="border border-border px-3 py-1.5 text-xs uppercase tracking-wider text-muted-foreground opacity-60">
                        🇪🇸 Español (base)
                    </span>
                    {DUB_LANGS.map((l) => {
                        const on = curLangs.includes(l.code);
                        return (
                            <button
                                key={l.code}
                                data-testid={`dub-lang-${l.code}`}
                                onClick={() => toggleLang(l.code)}
                                disabled={saveDub.isPending}
                                className={`flex items-center gap-2 border px-3 py-1.5 text-xs uppercase tracking-wider transition-colors ${
                                    on ? "border-primary bg-primary/15 text-primary"
                                       : "border-border text-muted-foreground hover:border-primary/60"
                                }`}
                            >
                                <StatusDot color={on ? "#39FF14" : "#555"} />
                                {l.label}
                                {l.rpm && <span className="text-[9px] text-status-yellow">{l.rpm}</span>}
                            </button>
                        );
                    })}
                </div>
                {dubbing?.enabled && (
                    <p className="mt-3 text-[11px] text-status-yellow">
                        Nota: se aplica a los videos NUEVOS que se publiquen desde ahora (reutiliza sus
                        visuales). Los videos antiguos no se pueden doblar porque sus clips ya se borraron.
                    </p>
                )}
            </Panel>

            <Panel title="Control de calidad de vídeo (QC)" testId="qc-panel" className="mb-6 border-primary/60">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="max-w-xl text-sm text-muted-foreground">
                        Antes de subir, cada vídeo (original y doblados) pasa un <b>control de calidad
                        automático</b>: verifica que tenga imagen y sonido, sin pantallas negras ni vídeo
                        congelado, con la duración y resolución correctas, y que el <b>idioma del audio</b>{" "}
                        coincida con el esperado (se transcribe con IA). Si algo falla, <b>rechaza el vídeo y
                        lo regenera</b> (hasta {qcCfg?.max_attempts || 3} intentos) antes de publicar. Solo
                        sube lo que esté <b>100% apto para producción</b>.
                    </p>
                    <button
                        data-testid="qc-toggle-btn"
                        onClick={() => saveQc.mutate({ enabled: !qcCfg?.enabled })}
                        disabled={saveQc.isPending}
                        className={`flex shrink-0 items-center gap-2 border px-4 py-2.5 text-xs uppercase tracking-wider transition-colors ${
                            qcCfg?.enabled
                                ? "border-primary bg-primary text-black"
                                : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                        }`}
                    >
                        <StatusDot color={qcCfg?.enabled ? "#000" : "#39FF14"} pulse={qcCfg?.enabled} />
                        {qcCfg?.enabled ? "QC: ACTIVO" : "Activar control de calidad"}
                    </button>
                </div>
            </Panel>

            <Panel title="Calendario de contenido" testId="calendar-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="label border-b border-border">
                                <th className="py-2 pr-4">Fecha</th>
                                <th className="py-2 pr-4">Canal</th>
                                <th className="py-2 pr-4">Título</th>
                                <th className="py-2 pr-4">Pilar</th>
                                <th className="py-2 pr-4">Fmt</th>
                                <th className="py-2 pr-4">Score</th>
                                <th className="py-2 pr-4">Estado</th>
                                <th className="py-2"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {calendar.map((it) => (
                                <tr
                                    key={it.id}
                                    className="border-b border-border/50 transition-colors hover:bg-accent"
                                    data-testid={`calendar-row-${it.id}`}
                                >
                                    <td className="py-2.5 pr-4 text-xs text-muted-foreground">{it.date}</td>
                                    <td className="py-2.5 pr-4 text-xs">{it.channel_name}</td>
                                    <td className="py-2.5 pr-4">{it.title}</td>
                                    <td className="py-2.5 pr-4 text-xs text-muted-foreground">{it.pillar}</td>
                                    <td className="py-2.5 pr-4 text-xs uppercase text-primary">{it.format}</td>
                                    <td className="py-2.5 pr-4">
                                        <span className="flex items-center gap-2 text-xs" style={{ color: scoreColor(it.score) }}>
                                            <StatusDot color={scoreColor(it.score)} /> {it.score}
                                        </span>
                                    </td>
                                    <td className="py-2.5 pr-4 text-xs uppercase text-muted-foreground">{it.status}</td>
                                    <td className="py-2.5">
                                        {it.status === "scheduled" && (
                                            <Btn
                                                testId={`produce-${it.id}`}
                                                variant="ghost"
                                                onClick={() => produce.mutate(it.id)}
                                                className="px-2 py-1"
                                            >
                                                <span className="flex items-center gap-1">
                                                    <Play size={12} /> Producir
                                                </span>
                                            </Btn>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {!calendar.length && (
                        <p className="py-6 text-sm text-muted-foreground">
                            Sin calendario. Genera un plan arriba (requiere un canal conectado con kit).
                        </p>
                    )}
                </div>
            </Panel>
        </div>
    );
}
